import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  ArrowLeft, 
  ClipboardCheck, 
  Users, 
  Calendar, 
  TrendingUp, 
  AlertTriangle,
  Shield,
  HardHat,
  MapPin,
  Building,
  UserCheck,
  BarChart3,
  RefreshCw
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FormAnalyticsPage() {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const token = localStorage.getItem('vaultai_token');
      const res = await axios.get(`${API}/forms/analytics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(res.data);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchAnalytics();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">No analytics data available</p>
      </div>
    );
  }

  const { summary, top_hazards, top_ppe, top_workers, top_locations, top_clients } = analytics;

  // Calculate bar widths based on max count
  const maxHazardCount = top_hazards[0]?.count || 1;
  const maxPpeCount = top_ppe[0]?.count || 1;
  const maxWorkerCount = top_workers[0]?.count || 1;

  return (
    <div className="space-y-6" data-testid="form-analytics-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/dashboard/forms">
            <Button variant="outline" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-foreground">FLHA Analytics</h1>
            <p className="text-muted-foreground">Safety form insights and trends</p>
          </div>
        </div>
        <Button variant="outline" onClick={handleRefresh} disabled={refreshing}>
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
        <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/20">
                <ClipboardCheck className="h-5 w-5 text-blue-400" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Total FLHAs</p>
                <p className="text-2xl font-bold text-blue-400">{summary.total.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500/10 to-green-600/5 border-green-500/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-500/20">
                <Calendar className="h-5 w-5 text-green-400" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Today</p>
                <p className="text-2xl font-bold text-green-400">{summary.today}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 border-purple-500/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-500/20">
                <TrendingUp className="h-5 w-5 text-purple-400" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">This Week</p>
                <p className="text-2xl font-bold text-purple-400">{summary.this_week}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-500/10 to-amber-600/5 border-amber-500/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/20">
                <BarChart3 className="h-5 w-5 text-amber-400" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">This Month</p>
                <p className="text-2xl font-bold text-amber-400">{summary.this_month}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-cyan-500/10 to-cyan-600/5 border-cyan-500/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-cyan-500/20">
                <Users className="h-5 w-5 text-cyan-400" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Workers</p>
                <p className="text-2xl font-bold text-cyan-400">{summary.unique_workers}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-pink-500/10 to-pink-600/5 border-pink-500/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-pink-500/20">
                <TrendingUp className="h-5 w-5 text-pink-400" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Avg/Day</p>
                <p className="text-2xl font-bold text-pink-400">{summary.avg_per_day}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={`bg-gradient-to-br ${summary.working_alone_pct > 20 ? 'from-red-500/10 to-red-600/5 border-red-500/20' : 'from-slate-500/10 to-slate-600/5 border-slate-500/20'}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${summary.working_alone_pct > 20 ? 'bg-red-500/20' : 'bg-slate-500/20'}`}>
                <UserCheck className={`h-5 w-5 ${summary.working_alone_pct > 20 ? 'text-red-400' : 'text-slate-400'}`} />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Working Alone</p>
                <p className={`text-2xl font-bold ${summary.working_alone_pct > 20 ? 'text-red-400' : 'text-slate-400'}`}>
                  {summary.working_alone_pct}%
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Top Hazards */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-500" />
              Top Hazards Identified
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {top_hazards.slice(0, 8).map((hazard, idx) => (
                <div key={hazard.id} className="flex items-center gap-3">
                  <span className="text-xs text-muted-foreground w-4">{idx + 1}</span>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm">{hazard.label}</span>
                      <span className="text-sm font-medium text-muted-foreground">{hazard.count.toLocaleString()}</span>
                    </div>
                    <div className="h-2 bg-secondary rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full transition-all"
                        style={{ width: `${(hazard.count / maxHazardCount) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Top PPE Used */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <HardHat className="h-5 w-5 text-green-500" />
              PPE Usage
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {top_ppe.slice(0, 8).map((ppe, idx) => (
                <div key={ppe.id} className="flex items-center gap-3">
                  <span className="text-xs text-muted-foreground w-4">{idx + 1}</span>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm">{ppe.label}</span>
                      <span className="text-sm font-medium text-muted-foreground">{ppe.count.toLocaleString()}</span>
                    </div>
                    <div className="h-2 bg-secondary rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-green-500 to-emerald-500 rounded-full transition-all"
                        style={{ width: `${(ppe.count / maxPpeCount) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Second Row */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Top Workers */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-500" />
              Most Active Workers
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {top_workers.slice(0, 8).map((worker, idx) => (
                <div key={worker.name} className="flex items-center justify-between p-2 rounded bg-secondary/30">
                  <div className="flex items-center gap-2">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                      idx === 0 ? 'bg-yellow-500/20 text-yellow-400' :
                      idx === 1 ? 'bg-slate-400/20 text-slate-400' :
                      idx === 2 ? 'bg-amber-600/20 text-amber-500' :
                      'bg-secondary text-muted-foreground'
                    }`}>
                      {idx + 1}
                    </span>
                    <span className="text-sm truncate max-w-[150px]">{worker.name}</span>
                  </div>
                  <span className="text-sm font-medium text-primary">{worker.count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Top Locations */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-red-500" />
              Top Locations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {top_locations.slice(0, 8).map((loc, idx) => (
                <div key={loc.location} className="flex items-center justify-between p-2 rounded bg-secondary/30">
                  <span className="text-sm truncate max-w-[180px]">{loc.location}</span>
                  <span className="text-sm font-medium text-muted-foreground">{loc.count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Top Clients */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building className="h-5 w-5 text-purple-500" />
              Top Clients
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {top_clients.slice(0, 8).map((client, idx) => (
                <div key={client.client} className="flex items-center justify-between p-2 rounded bg-secondary/30">
                  <span className="text-sm truncate max-w-[180px]">{client.client}</span>
                  <span className="text-sm font-medium text-muted-foreground">{client.count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="flex gap-4">
        <Link to="/dashboard/forms">
          <Button variant="outline">
            <ClipboardCheck className="w-4 h-4 mr-2" />
            View All Forms
          </Button>
        </Link>
        <Link to="/dashboard/forms/submissions">
          <Button variant="outline">
            <BarChart3 className="w-4 h-4 mr-2" />
            Browse Submissions
          </Button>
        </Link>
      </div>
    </div>
  );
}
